/**
 * Geschäftspartner anlegen (AP-31, Phase E).
 *
 * Drei Dinge, die der alte Dialog nicht konnte:
 *
 * 1. **Die Rolle.** Kunde, Lieferant oder beides — sie entscheidet, in welchem
 *    Belegtyp der Partner später auftaucht, und mindestens eine ist Pflicht.
 * 2. **Firma oder Person.** Bei einer Person entsteht der Name aus Vor- und
 *    Nachname und ist nicht tippbar (AP-31 4.4); das Feld zeigt, was
 *    herauskommt, damit niemand raten muss.
 * 3. **Gibt es den schon?** Gefragt wird, sobald der Name steht — und noch
 *    einmal beim Speichern. Der frühe Hinweis erspart das `409`, die Sperre im
 *    Backend fängt den Fall, in dem zwei Leute gleichzeitig tippen.
 *
 * Seit dem 5.10.2026 (Auftraggeber) ein vierter Weg: **nur die Webseite eingeben.**
 * „Von der Webseite lesen" holt Name, Anschrift, UID und Kontakt aus dem Impressum
 * und füllt den Dialog — ein Vorschlag, der vor dem Anlegen zu prüfen ist. Angeboten
 * wird das nur, wenn die KI des Betriebs eingeschaltet ist.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@symphenia/ui-components';
import { erfuelltRecht } from '@symphenia/shared-types';
import { KiArbeitet } from '@/components/ki/KiArbeitet';
import { useAuthStore } from '@/lib/auth.store';
import { clientsApi } from '../api/clients.api';
import { fehlertext } from '../api/fehler';
import { formatiereTelefon } from '@/lib/telefon';
import { normalisiereWebsite } from '@/lib/website';
import type {
  ClientType,
  CreateClientPayload,
  DuplicateCandidate,
  Salutation,
} from '../types/client.types';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Eine abgelegte Internetadresse (`lib/adress-ablage`): Der Dialog öffnet sich mit ihr als Webseite und
   * liest sie gleich — wo das Lesen angeboten wird. Sonst steht sie nur im Feld.
   */
  startWebsite?: string | null;
}

interface FormState {
  type: ClientType;
  isCustomer: boolean;
  isSupplier: boolean;
  name: string;
  salutation: Salutation | '';
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  website: string;
  notes: string;
  // Von der Webseite gelesen — die Anschrift wird nach dem Anlegen als Hauptadresse gespeichert.
  street: string;
  zip: string;
  city: string;
  country: string;
  vatId: string;
}

const EMPTY: FormState = {
  type: 'COMPANY',
  isCustomer: true,
  isSupplier: false,
  name: '',
  salutation: '',
  title: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  website: '',
  notes: '',
  street: '',
  zip: '',
  city: '',
  country: '',
  vatId: '',
};

/**
 * Die Ableitungsregel aus AP-31 4.4 — **dieselbe wie im Backend**.
 *
 * Sie steht hier ein zweites Mal, und das ist Absicht: Der Dialog zeigt sie
 * *an*, das Backend *entscheidet*. Weicht die Anzeige ab, ist das ein
 * Schönheitsfehler; hinge die Wahrheit an dieser Zeile, wäre es einer, den man
 * erst auf dem Beleg sieht.
 */
function abgeleiteterName(f: FormState): string {
  const vorname = f.firstName.trim();
  const nachname = f.lastName.trim();
  if (f.salutation === 'FAMILY' && !vorname && nachname) return `Familie ${nachname}`;
  return [vorname, nachname].filter(Boolean).join(' ');
}

export function CreateClientDialog({ open, onClose, onSuccess, startWebsite }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kandidaten, setKandidaten] = useState<DuplicateCandidate[]>([]);
  const [pruefeDubletten, setPruefeDubletten] = useState(false);
  const dublettenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Von der Webseite lesen
  const [webVerfuegbar, setWebVerfuegbar] = useState(false);
  const [webLaeuft, setWebLaeuft] = useState(false);
  const [webHinweis, setWebHinweis] = useState<string | null>(null);
  const [webQuellen, setWebQuellen] = useState<string[] | null>(null);
  // Die UID gehört zu den Zahlungsfeldern — ohne das Recht lehnt der Server die Anlage ab.
  const darfUid = erfuelltRecht('crm.client.payment.view', useAuthStore((s) => s.user?.permissions));

  useEffect(() => {
    if (!open) return;
    let aus = false;
    clientsApi
      .websiteReadingState()
      .then((r) => !aus && setWebVerfuegbar(r.data.data.available))
      // Kein Recht oder keine KI: Der Weg wird nicht angeboten, der Dialog bleibt, wie er war.
      .catch(() => !aus && setWebVerfuegbar(false));
    return () => {
      aus = true;
    };
  }, [open]);

  // Abgelegte Internetadresse: eintragen und, sobald feststeht, dass gelesen werden darf, einmal lesen.
  const liesRef = useRef<(adresse: string) => void>(() => undefined);
  const gestartet = useRef<string | null>(null);
  useEffect(() => {
    if (!open) gestartet.current = null;
    else if (startWebsite) setForm((f) => ({ ...f, website: startWebsite }));
  }, [open, startWebsite]);
  useEffect(() => {
    if (!open || !startWebsite || !webVerfuegbar || gestartet.current === startWebsite) return;
    gestartet.current = startWebsite;
    liesRef.current(startWebsite);
  }, [open, startWebsite, webVerfuegbar]);

  const istPerson = form.type === 'INDIVIDUAL';
  const anzeigeName = istPerson ? abgeleiteterName(form) : form.name.trim();
  const rolleFehlt = !form.isCustomer && !form.isSupplier;
  const nameFehlt = istPerson ? !form.lastName.trim() : !form.name.trim();

  // ── Gibt es den schon? ────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    if (dublettenTimer.current) clearTimeout(dublettenTimer.current);
    const name = anzeigeName;
    const email = form.email.trim();
    // Die UID kommt mit dem Lesen der Webseite in den Dialog — an ihr ist ein Partner am sichersten zu erkennen.
    const vatId = darfUid ? form.vatId.trim() : '';
    if (!name && !email && !vatId) {
      setKandidaten([]);
      return;
    }
    setPruefeDubletten(true);
    dublettenTimer.current = setTimeout(async () => {
      try {
        const { data } = await clientsApi.findDuplicates({
          name: name || undefined,
          email: email || undefined,
          vatId: vatId || undefined,
        });
        setKandidaten(data);
      } catch {
        // Die Prüfung ist ein Hinweis, keine Bedingung — ein Fehler hier darf
        // das Anlegen nicht aufhalten. Das `409` beim Speichern fängt den Fall.
        setKandidaten([]);
      } finally {
        setPruefeDubletten(false);
      }
    }, 500);
    return () => {
      if (dublettenTimer.current) clearTimeout(dublettenTimer.current);
    };
  }, [open, anzeigeName, form.email, form.vatId, darfUid]);

  if (!open) return null;

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [field]: value }));

  const schliessen = () => {
    setForm(EMPTY);
    setKandidaten([]);
    setError(null);
    setWebHinweis(null);
    setWebQuellen(null);
    onClose();
  };

  /** Füllt, was leer ist — was schon getippt wurde, bleibt stehen. */
  const liesWebseite = async (abgelegt?: string) => {
    const adresse = normalisiereWebsite(abgelegt ?? form.website);
    if (!adresse) return;
    setWebLaeuft(true);
    setWebHinweis(null);
    setError(null);
    try {
      const v = (await clientsApi.readWebsite(adresse)).data.data;
      if (!v.found) {
        setWebQuellen(null);
        setWebHinweis(t('clients.websiteReading.notFound'));
        return;
      }
      setWebQuellen(v.sources);
      setForm((f) => ({
        ...f,
        type: 'COMPANY',
        name: f.name.trim() || v.name || '',
        email: f.email.trim() || v.email || '',
        phone: f.phone.trim() || (v.phone ? formatiereTelefon(v.phone) : ''),
        website: v.website,
        street: v.street ?? '',
        zip: v.zip ?? '',
        city: v.city ?? '',
        country: v.country ?? 'AT',
        vatId: darfUid ? (v.vatId ?? '') : '',
      }));
    } catch (err: unknown) {
      setWebQuellen(null);
      setWebHinweis(fehlertext(err, t) || t('clients.websiteReading.failed'));
    } finally {
      setWebLaeuft(false);
    }
  };

  liesRef.current = (adresse) => void liesWebseite(adresse);

  const anlegen = async (allowDuplicate: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const payload: CreateClientPayload = {
        name: istPerson ? anzeigeName : form.name.trim(),
        type: form.type,
        isCustomer: form.isCustomer,
        isSupplier: form.isSupplier,
        ...(allowDuplicate && { allowDuplicate: true }),
      };
      if (istPerson) {
        if (form.salutation) payload.salutation = form.salutation;
        if (form.title.trim()) payload.title = form.title.trim();
        if (form.firstName.trim()) payload.firstName = form.firstName.trim();
        payload.lastName = form.lastName.trim();
      }
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.website.trim()) payload.website = normalisiereWebsite(form.website);
      if (form.notes.trim()) payload.notes = form.notes.trim();
      if (darfUid && form.vatId.trim()) payload.vatId = form.vatId.trim();

      const angelegt = (await clientsApi.create(payload)).data;
      // Die gelesene Anschrift als Hauptadresse — nur vollständig; der Partner steht dann schon.
      if (form.street.trim() && form.zip.trim() && form.city.trim()) {
        try {
          await clientsApi.addAddress(angelegt.id, {
            type: 'MAIN',
            street: form.street.trim(),
            zip: form.zip.trim(),
            city: form.city.trim(),
            country: /^[A-Za-z]{2}$/.test(form.country.trim()) ? form.country.trim().toUpperCase() : 'AT',
            isDefault: true,
          });
        } catch {
          setError(t('clients.websiteReading.addressFailed'));
          setForm((f) => ({ ...f, street: '', zip: '', city: '' }));
          onSuccess();
          return;
        }
      }
      schliessen();
      onSuccess();
    } catch (err: unknown) {
      const antwort = (
        err as { response?: { status?: number; data?: { message?: unknown } } }
      )?.response;
      const inhalt = antwort?.data?.message;
      // Das `409` der Dublettenprüfung trägt die Kandidaten im Körper.
      if (
        antwort?.status === 409 &&
        typeof inhalt === 'object' &&
        inhalt !== null &&
        'candidates' in inhalt
      ) {
        setKandidaten((inhalt as { candidates: DuplicateCandidate[] }).candidates);
        setError(null);
      } else {
        setError(fehlertext(err, t));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen onClose={schliessen} size="md" className="max-w-xl" padded={false} hideCloseButton>
      <div className="flex items-center justify-between border-b px-6 py-4">
        <h2 className="text-xl font-bold text-foreground">{t('clients.create')}</h2>
        <button
          onClick={schliessen}
          className="text-muted-foreground hover:text-foreground"
          aria-label={t('common.cancel')}
        >
          ✕
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void anlegen(false);
        }}
        className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5"
      >
        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Webseite zuerst: Mit ihr allein lässt sich der Rest vorschlagen. */}
        <div className={webVerfuegbar ? 'rounded-md border border-violet-500/40 bg-violet-500/5 px-3 py-3' : ''}>
          {webVerfuegbar && <p className="mb-2 text-sm text-foreground">{t('clients.websiteReading.hint')}</p>}
          <label className="mb-1 block text-sm font-medium text-foreground">{t('clients.website')}</label>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="url"
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
              onBlur={(e) => set('website', normalisiereWebsite(e.target.value))}
              onKeyDown={(e) => {
                // Enter im Webseiten-Feld liest — es legt nicht an.
                if (e.key === 'Enter' && webVerfuegbar) {
                  e.preventDefault();
                  void liesWebseite();
                }
              }}
              placeholder="www.beispiel.at"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            />
            {webVerfuegbar && (
              <button
                type="button"
                onClick={() => void liesWebseite()}
                disabled={webLaeuft || !form.website.trim()}
                className="shrink-0 rounded-md border border-violet-500/50 px-3 py-2 text-sm font-medium text-violet-700 hover:bg-violet-500/10 disabled:opacity-50 dark:text-violet-300"
              >
                {webLaeuft ? <KiArbeitet text={t('clients.websiteReading.reading')} /> : t('clients.websiteReading.read')}
              </button>
            )}
          </div>
          {webHinweis && <p className="mt-2 text-sm text-destructive">{webHinweis}</p>}
        </div>

        {/* Art */}
        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('clients.type')}
          </label>
          <select
            value={form.type}
            onChange={(e) => set('type', e.target.value as ClientType)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="COMPANY">{t('clients.types.COMPANY')}</option>
            <option value="INDIVIDUAL">{t('clients.types.INDIVIDUAL')}</option>
          </select>
        </div>

        {/* Rollen */}
        <div>
          <span className="mb-1 block text-sm font-medium text-foreground">
            {t('clients.role.label')}
          </span>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={form.isCustomer}
                onChange={(e) => set('isCustomer', e.target.checked)}
              />
              {t('clients.role.isCustomer')}
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={form.isSupplier}
                onChange={(e) => set('isSupplier', e.target.checked)}
              />
              {t('clients.role.isSupplier')}
            </label>
          </div>
          <p className={`mt-1 text-xs ${rolleFehlt ? 'text-destructive' : 'text-muted-foreground'}`}>
            {t('clients.role.hint')}
          </p>
        </div>

        {/* Name — Firma oder Person */}
        {istPerson ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('clients.person.salutation')}
                </label>
                <select
                  value={form.salutation}
                  onChange={(e) => set('salutation', e.target.value as Salutation | '')}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                >
                  <option value="">—</option>
                  <option value="MR">{t('clients.salutations.MR')}</option>
                  <option value="MRS">{t('clients.salutations.MRS')}</option>
                  <option value="FAMILY">{t('clients.salutations.FAMILY')}</option>
                  <option value="NONE">{t('clients.salutations.NONE')}</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('clients.person.title')}
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('clients.person.firstName')}
                </label>
                <input
                  type="text"
                  value={form.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-xl font-bold text-foreground"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('clients.person.lastName')} *
                </label>
                <input
                  type="text"
                  required
                  value={form.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-xl font-bold text-foreground"
                />
              </div>
            </div>
            <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
              <span className="text-muted-foreground">{t('clients.person.derivedName')} </span>
              <span className="text-xl font-bold text-foreground">{anzeigeName || '—'}</span>
            </div>
          </div>
        ) : (
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t('clients.companyName')} *
            </label>
            {/* Der Name ist die eine Belegzeile — er steht größer und fett. */}
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-xl font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )}

        {/* Gibt es den schon? */}
        {(pruefeDubletten || kandidaten.length > 0) && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/40">
            {pruefeDubletten && kandidaten.length === 0 ? (
              <span className="text-muted-foreground">{t('clients.duplicates.checking')}</span>
            ) : (
              <>
                <p className="font-medium text-foreground">{t('clients.duplicates.title')}</p>
                <p className="mt-0.5 text-muted-foreground">{t('clients.duplicates.intro')}</p>
                <ul className="mt-2 space-y-1">
                  {kandidaten.map((k) => (
                    <li key={k.id} className="flex items-center justify-between gap-2">
                      <span className="text-foreground">
                        <span className="font-mono text-xs text-muted-foreground">
                          {k.clientNumber}
                        </span>{' '}
                        {k.name}
                        <span className="ml-2 text-xs text-muted-foreground">
                          (
                          {k.matchedOn
                            .map((m) => t(`clients.duplicates.matchedOn.${m}`))
                            .join(', ')}
                          )
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          schliessen();
                          navigate(`/clients/${k.id}`);
                        }}
                        className="shrink-0 text-xs font-medium text-primary hover:underline"
                      >
                        {t('clients.duplicates.openExisting')}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {/* Kontakt */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t('clients.email')}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              {t('clients.phone')}
            </label>
            <input
              type="tel"
              value={form.phone}
              placeholder={t('clients.phonePlaceholder')}
              onChange={(e) => set('phone', e.target.value)}
              onBlur={(e) => set('phone', formatiereTelefon(e.target.value))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            />
          </div>
        </div>

        {/* Von der Webseite gelesen: Anschrift und UID — änderbar, bevor angelegt wird */}
        {webQuellen && (
          <div className="space-y-3 rounded-md border border-violet-500/40 bg-violet-500/5 px-3 py-3">
            <p className="text-sm font-medium text-foreground">{t('clients.websiteReading.resultTitle')}</p>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">{t('clients.websiteReading.street')}</label>
              <input type="text" value={form.street} onChange={(e) => set('street', e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground" />
            </div>
            <div className="grid grid-cols-[6rem_1fr_6rem] gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('clients.websiteReading.zip')}</label>
                <input type="text" value={form.zip} onChange={(e) => set('zip', e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('clients.websiteReading.city')}</label>
                <input type="text" value={form.city} onChange={(e) => set('city', e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('clients.websiteReading.country')}</label>
                <input type="text" maxLength={2} value={form.country} onChange={(e) => set('country', e.target.value.toUpperCase())} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground" />
              </div>
            </div>
            {darfUid && (
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">{t('clients.websiteReading.vatId')}</label>
                <input type="text" value={form.vatId} onChange={(e) => set('vatId', e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground" />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('clients.websiteReading.addressNote')} {t('clients.websiteReading.sources')}: {webQuellen.join(', ')}
            </p>
          </div>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            {t('clients.notes')}
          </label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
          />
        </div>

        {/* Aktionen */}
        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={schliessen}
            disabled={loading}
            className="rounded-md border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          {kandidaten.length > 0 && (
            <button
              type="button"
              onClick={() => void anlegen(true)}
              disabled={loading || rolleFehlt || nameFehlt}
              className="rounded-md border border-amber-400 px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-200 dark:hover:bg-amber-900/40"
            >
              {t('clients.duplicates.createAnyway')}
            </button>
          )}
          <button
            type="submit"
            disabled={loading || rolleFehlt || nameFehlt}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? t('common.loading') : t('common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
